import {Permission} from 'smag-commons/dto/api';
import {EntitySubType, EntityType} from 'smag-commons/cfg/hierarchy';
import {environment} from 'src/environments/environment';

/**
 * Returns whether the given {@link Permission permission} set includes <b>all</b> the given permissions.
 *
 * @param objectPerms A set of permissions of an object
 * @param perms Permissions to be checked
 * @return boolean Whether all the given permissions are "present" in the object's permission set
 */
export function can(objectPerms: Permission[], ...perms: Permission[]): boolean {
    const objPerms = objectPerms || [];
    return objPerms.includes(Permission.ALL) || perms.every(p => objPerms.includes(p)) || !environment.permissionsEnabled;
}

/**
 * Returns all the permissions {@link PermissionPattern patterns} that can be set for a given
 * {@link HierarchyEntity resource}.
 *
 * @param resource The resource
 * @returns PermissionPattern[] All permission patterns for the resource
 */
export function allResourcePermissionPatterns(resource: {
    type: EntityType,
    sub_type?: EntitySubType
}): PermissionPattern[] {
    return ALL_RESOURCE_PERMISSION_PATTERNS;
}

export interface PermissionPattern {
    permissions: Permission[];
    name: string;
    icon?: string;
}

export const ALL_RESOURCE_PERMISSION_PATTERNS: PermissionPattern[] = [
    {
        permissions: [Permission.READ],
        name: 'READ'
    },
    {
        permissions: [Permission.CREATE, Permission.READ, Permission.UPDATE],
        name: 'CREATE, READ, UPDATE'
    },
    {
        permissions: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
        name: 'CREATE, READ, UPDATE, DELETE'
    },
    {
        permissions: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE, Permission.OPERATE, Permission.PERMISSIONS],
        name: 'CREATE, READ, UPDATE, DELETE, OPERATE, PERMISSIONS (ALL)'
    }
];

export const ALL_CONNECTOR_PERMISSION_PATTERNS: PermissionPattern[] = [
    {
        permissions: [Permission.READ],
        name: 'READ'
    },
    {
        permissions: [Permission.READ, Permission.UPDATE],
        name: 'READ, UPDATE'
    },
    {
        permissions: [Permission.READ, Permission.UPDATE, Permission.PERMISSIONS],
        name: 'READ, UPDATE, PERMISSIONS (ALL)'
    }
];

export const ALL_USER_PERMISSION_PATTERNS: PermissionPattern[] = [
    {
        permissions: [Permission.READ],
        name: 'READ'
    },
    {
        permissions: [Permission.CREATE, Permission.READ, Permission.UPDATE],
        name: 'CREATE, READ, UPDATE'
    },
    {
        permissions: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE],
        name: 'CREATE, READ, UPDATE, DELETE'
    },
    {
        permissions: [Permission.CREATE, Permission.READ, Permission.UPDATE, Permission.DELETE, Permission.PERMISSIONS],
        name: 'CREATE, READ, UPDATE, DELETE, PERMISSIONS (ALL)'
    }
];
